/**
 * 3D 분석 머리줄 도구 — 기록 내보내기(CSV) · 달력으로 선택 · 개체·날짜 비교 (grosync-local 3D 분석 탭과 같은 구성).
 */
import React, { useMemo, useState } from 'react';
import type { DeviceInfo, GrowthRecord } from '@grosync/common';
import { Sym } from '../../ui';
import { getPhotoUrl } from '../../api/growth';
import { useAuthedUrl, type Growth3dResult } from './api';
import { useDeviceShots } from './shots';
import { TRAITS, type DayTemp } from './Trends';

const day = (r: GrowthRecord): string => r.recordedAt.slice(0, 10);

function statusText(res: Growth3dResult | null | undefined): string {
  if (!res) return '3D 처리 전';
  return (res.height.warnings ?? []).length > 0 ? '확인 필요' : '분석 완료';
}

/* ---- 기록 내보내기 ------------------------------------------------------ */

export function exportCsv(
  deviceName: string,
  shots: GrowthRecord[],
  results: Map<string, Growth3dResult | null | undefined>,
  temps: Map<string, DayTemp>,
): void {
  if (!shots.length) return;
  const head = [
    '날짜',
    '장비',
    '상태',
    ...TRAITS.map((t) => `${t.label}(${t.unit})`),
    '평균온도(C)',
    '최저온도(C)',
    '최고온도(C)',
  ];
  const lines = shots.map((r) => {
    const res = results.get(r.id);
    const t = temps.get(day(r));
    const cells = [
      day(r),
      deviceName,
      statusText(res),
      ...TRAITS.map((m) => {
        const v = res ? m.pick(res) : null;
        return v == null ? '' : v.toFixed(m.digits);
      }),
      t ? t.avg.toFixed(1) : '',
      t ? t.min.toFixed(1) : '',
      t ? t.max.toFixed(1) : '',
    ];
    // 값에 쉼표가 들어가면 열이 밀린다
    return cells.map((c) => (/[",\r\n]/.test(c) ? `"${c.replace(/"/g, '""')}"` : c)).join(',');
  });
  // BOM 을 붙여야 엑셀이 한글을 깨뜨리지 않는다
  const blob = new Blob(['﻿' + [head.join(','), ...lines].join('\n')], {
    type: 'text/csv;charset=utf-8;',
  });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `생육3D_${deviceName}_${day(shots[0])}_${day(shots[shots.length - 1])}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

/** 머리줄 단추. 기존 생육 페이지의 'CSV 내보내기' 단추와 같은 모양이다. */
export function ToolBtn({
  icon,
  onClick,
  children,
}: {
  icon: string;
  onClick: () => void;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <button
      onClick={onClick}
      style={{
        height: 38,
        padding: '0 15px',
        borderRadius: 9999,
        cursor: 'pointer',
        background: 'transparent',
        border: '1px solid var(--md-sys-color-outline)',
        color: 'var(--md-sys-color-on-surface)',
        font: '600 13px/1 var(--gs-font-kr)',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
      }}
    >
      <Sym name={icon} size={17} />
      {children}
    </button>
  );
}

/* ---- 공통 ---------------------------------------------------------------- */

const overlay: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 60,
  background: 'rgba(0,0,0,.34)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 16,
};

function RoundBtn({
  icon,
  onClick,
  disabled,
}: {
  icon: string;
  onClick: () => void;
  disabled?: boolean;
}): React.ReactElement {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        width: 32,
        height: 32,
        borderRadius: 9999,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: disabled ? 'default' : 'pointer',
        border: '1px solid var(--md-sys-color-outline-variant)',
        background: 'transparent',
        opacity: disabled ? 0.35 : 1,
        color: 'var(--md-sys-color-on-surface-variant)',
      }}
    >
      <Sym name={icon} size={17} />
    </button>
  );
}

function Legend({ color, text }: { color: string; text: string }): React.ReactElement {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
      <i
        style={{
          width: 11,
          height: 11,
          borderRadius: 4,
          background: color,
          display: 'inline-block',
        }}
      />
      {text}
    </span>
  );
}

/* ---- 달력 ---------------------------------------------------------------- */

export function CalendarPopup({
  shots,
  results,
  selected,
  onPick,
  onClose,
}: {
  shots: GrowthRecord[];
  results: Map<string, Growth3dResult | null | undefined>;
  selected: string | null;
  onPick: (id: string) => void;
  onClose: () => void;
}): React.ReactElement {
  const byDate = useMemo(() => new Map(shots.map((r) => [day(r), r])), [shots]);
  const months = useMemo(() => [...new Set(shots.map((r) => day(r).slice(0, 7)))].sort(), [shots]);
  const current = shots.find((r) => r.id === selected);
  const [month, setMonth] = useState(
    current ? day(current).slice(0, 7) : (months[months.length - 1] ?? ''),
  );
  const idx = months.indexOf(month);
  const [y, m] = month.split('-').map(Number);
  const days = new Date(y, m, 0).getDate();
  const lead = new Date(y, m - 1, 1).getDay();

  return (
    <div role="dialog" onClick={onClose} style={overlay}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(380px, 100%)',
          padding: 18,
          borderRadius: 20,
          background: 'var(--md-sys-color-surface-container-lowest)',
          border: '1px solid var(--md-sys-color-outline-variant)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <RoundBtn
            icon="chevron_left"
            disabled={idx <= 0}
            onClick={() => setMonth(months[idx - 1])}
          />
          <div
            style={{
              flex: 1,
              textAlign: 'center',
              font: '600 15px/20px var(--gs-font-kr)',
              color: 'var(--md-sys-color-on-surface)',
            }}
          >
            {y}년 {m}월
          </div>
          <RoundBtn
            icon="chevron_right"
            disabled={idx >= months.length - 1}
            onClick={() => setMonth(months[idx + 1])}
          />
          <RoundBtn icon="close" onClick={onClose} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
          {['일', '월', '화', '수', '목', '금', '토'].map((d) => (
            <div
              key={d}
              style={{
                textAlign: 'center',
                font: '500 10px/18px var(--gs-font-kr)',
                color: 'var(--md-sys-color-on-surface-variant)',
              }}
            >
              {d}
            </div>
          ))}
          {Array.from({ length: lead }, (_, i) => (
            <div key={`p${i}`} />
          ))}
          {Array.from({ length: days }, (_, i) => {
            const date = `${month}-${String(i + 1).padStart(2, '0')}`;
            const r = byDate.get(date);
            const active = !!r && r.id === selected;
            const has3d = !!r && !!results.get(r.id);
            return (
              <button
                key={date}
                disabled={!r}
                onClick={() => {
                  if (r) {
                    onPick(r.id);
                    onClose();
                  }
                }}
                title={
                  !r
                    ? '촬영 없음'
                    : has3d
                      ? `${results.get(r.id)?.height.height_mm?.toFixed(1) ?? '—'} mm`
                      : '3D 처리 전'
                }
                style={{
                  height: 38,
                  borderRadius: 10,
                  cursor: r ? 'pointer' : 'default',
                  border: active
                    ? '2px solid var(--md-sys-color-primary)'
                    : '1px solid transparent',
                  background: !r
                    ? 'transparent'
                    : has3d
                      ? 'var(--md-sys-color-primary-container)'
                      : 'var(--md-sys-color-surface-container)',
                  color: !r
                    ? 'var(--md-sys-color-outline)'
                    : has3d
                      ? 'var(--md-sys-color-on-primary-container)'
                      : 'var(--md-sys-color-on-surface-variant)',
                  font: `${active ? 700 : 500} 12px/1 var(--gs-font-kr)`,
                }}
              >
                {i + 1}
              </button>
            );
          })}
        </div>
        <div
          style={{
            marginTop: 12,
            display: 'flex',
            gap: 14,
            font: '400 10px/14px var(--gs-font-kr)',
            color: 'var(--md-sys-color-on-surface-variant)',
          }}
        >
          <Legend color="var(--md-sys-color-primary-container)" text="3D 분석" />
          <Legend color="var(--md-sys-color-surface-container)" text="3D 처리 전" />
        </div>
      </div>
    </div>
  );
}

/* ---- 비교 ---------------------------------------------------------------- */

interface Side {
  deviceId: string;
  recordId: string;
}

const selStyle: React.CSSProperties = {
  height: 34,
  padding: '0 9px',
  borderRadius: 10,
  border: '1px solid var(--md-sys-color-outline-variant)',
  background: 'var(--md-sys-color-surface-container-lowest)',
  color: 'var(--md-sys-color-on-surface)',
  font: '500 12px/1 var(--gs-font-kr)',
  minWidth: 0,
};

function CompareSide({
  label,
  side,
  devices,
  shots,
  result,
  onDevice,
  onRecord,
}: {
  label: string;
  side: Side;
  devices: DeviceInfo[];
  shots: GrowthRecord[];
  result: Growth3dResult | null | undefined;
  onDevice: (id: string) => void;
  onRecord: (id: string) => void;
}): React.ReactElement {
  const rec = shots.find((r) => r.id === side.recordId);
  const photo = useAuthedUrl(rec?.photoPath ? getPhotoUrl(rec.photoPath) : null, 'photo');
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 9 }}>
        <span
          style={{
            font: '600 11px/1 var(--gs-font-kr)',
            color: 'var(--md-sys-color-on-surface-variant)',
          }}
        >
          {label}
        </span>
        <select value={side.deviceId} onChange={(e) => onDevice(e.target.value)} style={selStyle}>
          {devices.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name || d.id}
            </option>
          ))}
        </select>
        <select
          value={side.recordId}
          onChange={(e) => onRecord(e.target.value)}
          style={{ ...selStyle, flex: 1 }}
        >
          {shots.map((r) => (
            <option key={r.id} value={r.id}>
              {day(r)}
            </option>
          ))}
        </select>
      </div>
      <div
        style={{
          width: '100%',
          aspectRatio: '4 / 3',
          borderRadius: 13,
          overflow: 'hidden',
          background: 'var(--md-sys-color-surface-container)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {photo.src ? (
          <img
            src={photo.src}
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        ) : (
          <Sym
            name={rec ? 'photo_camera' : 'no_photography'}
            size={28}
            style={{ color: 'var(--md-sys-color-outline)' }}
          />
        )}
      </div>
      <div
        className="gs-num"
        style={{
          marginTop: 8,
          font: '700 18px/22px var(--gs-font-kr)',
          color: 'var(--md-sys-color-on-surface)',
        }}
      >
        {result?.height.height_mm != null ? `${result.height.height_mm.toFixed(1)} mm` : '—'}
      </div>
    </div>
  );
}

const cell = (align: 'left' | 'right'): React.CSSProperties => ({
  padding: '9px 8px',
  textAlign: align,
  borderBottom: '1px solid var(--md-sys-color-outline-variant)',
  color: 'var(--md-sys-color-on-surface)',
});

export function ComparePanel({
  devices,
  deviceId,
  shots,
  selected,
  onClose,
}: {
  devices: DeviceInfo[];
  /** 지금 보고 있는 장비 · 날짜 — '비교' 쪽 처음 값 */
  deviceId: string;
  shots: GrowthRecord[];
  selected: string | null;
  onClose: () => void;
}): React.ReactElement {
  // 기준 = 같은 장비 약 일주일 전(없으면 첫 날짜) · 비교 = 지금 고른 날짜
  const bRec = shots.find((r) => r.id === selected) ?? shots[shots.length - 1];
  const before = shots.filter((r) => bRec && day(r) < day(bRec));
  const aRec = before[Math.max(0, before.length - 7)] ?? before[0] ?? bRec;
  const [a, setA] = useState<Side>({ deviceId, recordId: aRec?.id ?? '' });
  const [b, setB] = useState<Side>({ deviceId, recordId: bRec?.id ?? '' });
  const aData = useDeviceShots(a.deviceId);
  const bData = useDeviceShots(b.deviceId);

  // 장비를 바꾸면 같은 날짜가 있으면 그 날, 없으면 그 장비의 마지막 날짜
  const pickDevice = (set: (s: Side) => void, cur: Side, curShots: GrowthRecord[], id: string) => {
    const curDay = curShots.find((r) => r.id === cur.recordId);
    set({ deviceId: id, recordId: curDay ? `@${day(curDay)}` : '' });
  };
  // 장비를 바꾼 직후에는 기록 id 대신 '@날짜'를 들고 있다가, 새 장비 기록이 오면 id 로 바꾼다
  const resolve = (side: Side, list: GrowthRecord[]): GrowthRecord | undefined => {
    if (!side.recordId.startsWith('@')) return list.find((r) => r.id === side.recordId);
    const want = side.recordId.slice(1);
    return list.find((r) => day(r) === want) ?? list[list.length - 1];
  };
  const ar = resolve(a, aData.shots);
  const br = resolve(b, bData.shots);
  const ares = ar ? aData.results.get(ar.id) : undefined;
  const bres = br ? bData.results.get(br.id) : undefined;
  const nameOf = (id: string): string => devices.find((d) => d.id === id)?.name || id;

  const sameDevice = !!ar && !!br && a.deviceId === b.deviceId;
  const sameDate = !!ar && !!br && day(ar) === day(br);
  const days = ar && br ? Math.round((Date.parse(day(br)) - Date.parse(day(ar))) / 86_400_000) : 0;
  // 장비가 다르면 '생장' 이라고 쓰면 안 된다 — 차이의 원인이 섞여 있다
  const heading =
    !ar || !br
      ? '비교할 기록을 고르세요'
      : sameDevice
        ? `${nameOf(a.deviceId)} · ${Math.abs(days)}일 동안의 생장`
        : sameDate
          ? `${nameOf(a.deviceId)} ↔ ${nameOf(b.deviceId)} · 같은 날 개체 차이`
          : `${nameOf(a.deviceId)}(${day(ar)}) ↔ ${nameOf(b.deviceId)}(${day(br)})`;
  const caveat =
    !ar || !br
      ? ''
      : sameDevice
        ? '차이 = 나중 날짜 − 기준 날짜'
        : sameDate
          ? '차이 = 비교 개체 − 기준 개체'
          : '개체도 날짜도 다릅니다 — 생장과 개체 차이가 섞여 있습니다';

  return (
    <div role="dialog" onClick={onClose} style={overlay}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(940px, 100%)',
          maxHeight: '90vh',
          overflowY: 'auto',
          padding: 20,
          borderRadius: 20,
          background: 'var(--md-sys-color-surface-container-lowest)',
          border: '1px solid var(--md-sys-color-outline-variant)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                font: '600 16px/21px var(--gs-font-kr)',
                color: 'var(--md-sys-color-on-surface)',
              }}
            >
              {heading}
            </div>
            {caveat !== '' && (
              <div
                style={{
                  marginTop: 3,
                  font: '400 11px/16px var(--gs-font-kr)',
                  color: 'var(--md-sys-color-on-surface-variant)',
                }}
              >
                {caveat}
              </div>
            )}
          </div>
          <RoundBtn icon="close" onClick={onClose} />
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(290px, 1fr))',
            gap: 16,
          }}
        >
          <CompareSide
            label="기준"
            side={{ deviceId: a.deviceId, recordId: ar?.id ?? '' }}
            devices={devices}
            shots={aData.shots}
            result={ares}
            onDevice={(id) => pickDevice(setA, a, aData.shots, id)}
            onRecord={(id) => setA({ ...a, recordId: id })}
          />
          <CompareSide
            label="비교"
            side={{ deviceId: b.deviceId, recordId: br?.id ?? '' }}
            devices={devices}
            shots={bData.shots}
            result={bres}
            onDevice={(id) => pickDevice(setB, b, bData.shots, id)}
            onRecord={(id) => setB({ ...b, recordId: id })}
          />
        </div>

        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            marginTop: 18,
            font: '400 12px/1 var(--gs-font-kr)',
          }}
        >
          <thead>
            <tr>
              {['지표', '기준', '비교', '차이'].map((h, i) => (
                <th
                  key={h}
                  style={{
                    padding: '9px 8px',
                    textAlign: i === 0 ? 'left' : 'right',
                    borderBottom: '1px solid var(--md-sys-color-outline-variant)',
                    font: '500 11px/1 var(--gs-font-kr)',
                    color: 'var(--md-sys-color-on-surface-variant)',
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {TRAITS.map((t) => {
              const va = ares ? (t.pick(ares) ?? null) : null;
              const vb = bres ? (t.pick(bres) ?? null) : null;
              const d = va != null && vb != null ? vb - va : null;
              return (
                <tr key={t.key}>
                  <td style={cell('left')}>{t.label}</td>
                  <td style={cell('right')} className="gs-num">
                    {va == null ? '—' : va.toFixed(t.digits)} {t.unit}
                  </td>
                  <td style={cell('right')} className="gs-num">
                    {vb == null ? '—' : vb.toFixed(t.digits)} {t.unit}
                  </td>
                  <td
                    className="gs-num"
                    style={{
                      ...cell('right'),
                      fontWeight: 600,
                      color:
                        d == null || d === 0
                          ? 'var(--md-sys-color-on-surface-variant)'
                          : d > 0
                            ? 'var(--md-sys-color-primary)'
                            : 'var(--gs-warning, var(--md-sys-color-error))',
                    }}
                  >
                    {d == null
                      ? '—'
                      : d === 0
                        ? '변화 없음'
                        : `${d > 0 ? '▲' : '▼'} ${Math.abs(d).toFixed(t.digits)} ${t.unit}`}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
